<template>
  <page-loader :isLoading="isLoading.taskTargets">
    <drawer-form-layout
      class="task-form"
      :model="model"
      :rules="rules"
      @finish="onFinish"
      @validate="errors.onValidate"
    >
      <template #header>{{ formHeader }}</template>
      <template #instruction>
        <p>{{ formInstruction }}</p>
      </template>
      <template #default>
        <textarea-item
          v-model="model.description"
          name="description"
          :errors="errors"
          :max-length="10000"
          placeholder="Сформулируйте задачу *"
        />
        <select-searchable-item
          v-model="model.userId"
          name="userId"
          :errors="errors"
          :get-items="api.projectExecutor.index"
          :default-search-params="userSearchParams"
          :get-options="
            (items) =>
              items.map((item) => ({
                value: item.userId,
                label: item.fullName,
              }))
          "
          placeholder="Выберите сотрудника"
          entity="executors"
          searchable-property="name"
        />

        <!-- <div v-show="isStudentExecutor"> -->
        <!-- TODO: поле не обязательное. Нужно добавить возможность отменить выбор пункта -->
        <radio-list
          v-model="model.projectTaskTargetId"
          :items="ProjectTaskTargets.toRadioList(taskTargets)"
        />

        <select-item
          v-model="model.projectTaskRoleId"
          name="projectTaskRoleId"
          :disabled="null === model.projectTaskTargetId"
          :errors="errors"
          :options="
            taskRoles.map((role) => ({ value: role.id, label: role.name }))
          "
          placeholder="Выберите роль"
        />
        <!-- </div> -->

        <h3>Контрольные точки</h3>

        <check-point-block
          v-for="(_, index) in model.checkpoints"
          v-model="model.checkpoints[index]"
          :key="index"
          :rules="nestedRules.checkpoints.fields[index].fields"
          :countCheckpoints="model.checkpoints.length"
          :index="index"
          name="checkpoints"
          @remove="removeCheckpoint"
          @change-no-matter="changeNoMatter"
        />

        <div class="form__add-checkpoint">
          <adding-control
            text="Добавить контрольную точку"
            @click="addCheckpoint"
          />
        </div>
      </template>
      <template #controls>
        <!-- TODO: упростить верстку кнопок -->
        <a-form-item class="form__buttons">
          <a-row :align="'bottom'" justify="space-between">
            <a-col class="buttons__delete">
              <a
                v-if="null !== model.id && canBeDeleted"
                @click="(event) => $emit('delete-action', event)"
              >
                Удалить задачу
              </a>
            </a-col>
            <a-col class="buttons__save">
              <c-button-primary html-type="submit" :loading="isLoading.task">
                {{ saveButtonText }}
              </c-button-primary>
            </a-col>
          </a-row>
        </a-form-item>
      </template>
    </drawer-form-layout>
  </page-loader>
</template>

<script setup>
  import dayjs from 'dayjs';
  import { computed, onMounted, reactive, ref, watch } from 'vue';
  import { useApi } from '@/api/use-api';
  import CheckPointBlock from '../CheckPoints/CheckPointBlock.vue';
  import { ProjectTask } from './adapters/project-task';
  import { ProjectTaskTargets } from './adapters/project-task-targets';
  import { Checkpoint } from './models/checkpoint';
  import DrawerFormLayout from '@/components/form/layouts/DrawerFormLayout.vue';
  import AddingControl from '@/components/controls/components/AddingControl.vue';
  import RadioList from '@/components/form/components/RadioList.vue';
  import SelectItem from '@/components/form/components/SelectItem.vue';
  import TextareaItem from '@/components/form/components/TextareaItem.vue';
  import ErrorsHandler from '@/components/form/errors-handler';
  import CButtonPrimary from '@/components/UI/CButtonPrimary.vue';
  import { USER_ROLE_LEADER } from '@/api/models/UserRole';
  import PageLoader from '@/components/loaders/PageLoader.vue';
  import SelectSearchableItem from '@/components/form/components/SelectSearchableItem.vue';
  import { useRoute } from 'vue-router';

  const props = defineProps({
    /**
     * Идентификатор родительской задачи. Если задан, то добавляется подзадача.
     * Используется только для добавления новой задачи.
     */
    parentId: {
      type: Number,
      default: null,
    },

    /**
     * Идентификатор проекта. Используется только для добавления новой задачи.
     */
    projectId: {
      type: Number,
      default: null,
    },

    /**
     * Объект задачи / подзадачи.
     * Параметр указывается только при редактировании задачи / подзадачи.
     */
    task: Object,
  });

  const emit = defineEmits([
    /**
     * Событие срабатывает при сохранении / обновлении задачи.
     */
    'updated',

    /**
     * Событие срабатывает при нажатии на ссылку удаления задачи.
     */
    'delete-action',
  ]);

  // Преобразование данных задачи в модель формы.
  const model = reactive({
    projectId: props.projectId,
    parentId: props.parentId,
    ...ProjectTask.toForm(props.task),
  });

  const formHeader = computed(() => {
    const action = null === model.id ? 'Добавить' : 'Редактировать';
    return `${action} ${ProjectTask.getTypeLabel(model.parentId)}`;
  });

  const formInstruction = computed(() => {
    if (null === model.id) {
      return `Заполните поля ниже, чтобы добавить ${ProjectTask.getTypeLabel(
        model.parentId
      )}.`;
    } else {
      return 'Внесите изменения и сохраните их. Если изменений нет, просто закройте это окно.';
    }
  });

  const saveButtonText = computed(() => {
    if (null === model.id) {
      return 'Добавить';
    } else {
      return 'Сохранить';
    }
  });

  const api = useApi();
  const route = useRoute();
  const taskTargets = ref([]);
  const taskRoles = ref([]);

  // метки загрузки данных
  const isLoading = reactive({
    taskTargets: true,
    taskRoles: false,
    task: false,
  });

  const userSearchParams = computed(
    () =>
      new URLSearchParams({
        'per-page': '100',
        projectId: route.params.id,
        sort: 'lastName,firstName,middleName',
      })
  );

  const removedCheckpoints = ref([]); // массив идентификаторов удаленных контрольных точек

  // TODO: переместить проверку прав доступа выше по иерархии компонентов, на уровень страницы
  const isLeader = localStorage?.getItem('currentRole') === USER_ROLE_LEADER;

  // const isStudentExecutor = ref(false);

  const getTaskTargets = async () => {
    isLoading.taskTargets = true;

    try {
      const response = await api.projectTaskTarget.index();
      taskTargets.value = response.projectTaskTargets.items;
    } catch (error) {
      // TODO: добавить процесс отображения общих ошибок сайта
      console.log(error);
    } finally {
      isLoading.taskTargets = false;
    }
  };

  const resetTaskRole = () => {
    model.projectTaskRoleId = null;
  };

  const getTaskRoles = async () => {
    isLoading.taskRoles = true;
    taskRoles.value = [];

    try {
      const response = await api.projectTaskRole.index({
        projectTaskTargetId: model.projectTaskTargetId,
      });

      taskRoles.value = response.projectTaskRoles.items;
    } catch (error) {
      // TODO: добавить процесс отображения общих ошибок сайта
      console.log(error);
    } finally {
      isLoading.taskRoles = false;
    }
  };

  onMounted(() => {
    getTaskTargets();

    if (null !== model.projectTaskTargetId) {
      getTaskRoles();
    }
  });

  watch(
    () => model.projectTaskTargetId,
    () => {
      resetTaskRole();
      getTaskRoles();
    }
  );

  // watch(
  //   () => model.userId,
  //   () => {
  //     users.value.forEach(function (user) {
  //       if (user.userId === model.userId) {
  //         isStudentExecutor.value = user.isStudent;
  //       }
  //     });
  //   }
  // );

  const rules = {
    description: {
      type: 'string',
      required: true,
      min: 2,
      message: 'Необходимо сформулировать задачу, не менее 2 символов',
      trigger: 'change',
    },
    userId: {
      type: 'number',
      required: true,
      message: 'Необходимо выбрать исполнителя задачи',
      trigger: 'change',
    },
    projectTaskRoleId: {
      type: 'number',
      trigger: 'change',
    },
  };

  const nestedRules = reactive({
    checkpoints: {
      type: 'array',
      required: true,
      fields: model.checkpoints.map((checkpoint) =>
        Checkpoint.rulesBlock(checkpoint)
      ),
    },
  });

  const errors = new ErrorsHandler({
    description: '',
    userId: '',
    projectTaskTargetId: '',
    projectTaskRoleId: '',
    // checkpoints: '',
  });

  const addCheckpoint = () => {
    model.checkpoints.push(Checkpoint.generate());
    nestedRules.checkpoints.fields.push(Checkpoint.rulesBlock());
  };

  const removeCheckpoint = (index) => {
    const removed = model.checkpoints.splice(index, 1);
    nestedRules.checkpoints.fields.splice(index, 1);

    // вариант цикла for-in не работает из-за реактивности
    for (let i = 0; i < removed.length; i++) {
      if (null !== removed[i].id) {
        removedCheckpoints.value.push(removed[i].id);
      }
    }
  };

  /**
   * Обновление правила валидации связанной даты в зависимости от значения флага "Неважно".
   * @param value Значение флага "Неважно"
   * @param index Номер контрольной точки в списке
   */
  const changeNoMatter = (value, index) => {
    if (value) {
      model.checkpoints[index].date = null;
    }

    nestedRules.checkpoints.fields[index].fields.date.required = !value;
  };

  const saveCheckpoints = async (taskId, checkpoints) => {
    if (undefined === checkpoints) {
      return;
    }

    for (let i = 0; i < checkpoints.length; i++) {
      await api.projectManagementPage.saveCheckpoint({
        id: checkpoints[i].id ?? null,
        taskId,
        description: checkpoints[i].result,
        date: checkpoints[i].noMatter
          ? null
          : dayjs(checkpoints[i].date).format('YYYY-MM-DD'),
      });
    }
  };

  const onFinish = async () => {
    try {
      isLoading.task = true;

      if (null === model.id) {
        const response = await api.projectTask.create(
          ProjectTask.fromForm(model)
        );
        model.id = response.id;
      } else {
        await api.projectTask.update(model.id, ProjectTask.fromForm(model));
      }

      await saveCheckpoints(model.id, model.checkpoints);

      for (let i = 0; i < removedCheckpoints.value.length; i++) {
        await api.projectManagementPage.deleteCheckpoint(
          removedCheckpoints.value[i]
        );
      }

      emit('updated');
    } catch (error) {
      console.log(error);
      // errors.handleApiErrors(error);
    } finally {
      isLoading.task = false;
    }
  };

  const canBeDeleted = computed(() => {
    return isLeader || null !== model.parentId;
  });
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_colors.scss' as *;

  .task-form {
    .form {
      &__add-checkpoint {
        margin-top: 20px;
        margin-left: 15px;

        a {
          border-bottom: 1px solid;
          color: $color-link-primary;
          text-decoration: none;
        }
      }

      &__buttons {
        margin-top: 36px;

        .buttons {
          &__delete {
            a {
              border-bottom: 1px solid;
              color: $color-text-accent-red;
              font: 400 14px/20px 'Nunito Sans';
              text-decoration: none;
            }
          }

          &__save {
            margin-bottom: 0;
          }
        }
      }
    }
  }
</style>
